import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Landing from './pages/Landing';
import Inscription from './pages/Inscription';
import Connexion from './pages/Connexion';
import Feed from './pages/Feed';
import Detail from './pages/Detail';
import CreerActivite from './pages/CreerActivite';
import ModifierActivite from './pages/ModifierActivite';
import Profil from './pages/Profil';
import Notation from './pages/Notation';
import Invite from './pages/Invite';
import CarteActivites from './pages/CarteActivites';
import CGU from './pages/CGU';
import ProfilPublic from './pages/ProfilPublic';
import Admin from './pages/Admin';
import DevenirOrganisateur from './pages/DevenirOrganisateur';
import Pro from './pages/Pro';
import InscriptionPro from './pages/InscriptionPro';
import NavBar from './components/NavBar';
import Splash from './components/Splash';
import { getUser, refreshUser } from './auth';
import { supabase } from './supabase';
import './styles/globals.css';

function PrivateRoute({ children }) {
  const user = getUser();
  return user ? children : <Navigate to="/connexion" />;
}

async function enregistrerServiceWorker() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return null;
  try {
    const reg = await navigator.serviceWorker.register('/sw.js');
    return reg;
  } catch (e) { return null; }
}

function App() {
  const [showSplash, setShowSplash] = useState(true);
  const [notifPhoto, setNotifPhoto] = useState(null);
  const [showNotifBanner, setShowNotifBanner] = useState(false);
  const [recapHebdo, setRecapHebdo] = useState(null);

  useEffect(() => {
    refreshUser();
    verifierActivitesTerminees();
    enregistrerServiceWorker();
    verifierRecapHebdo();
    const user = getUser();
    if (user && 'Notification' in window && Notification.permission === 'default') {
      setTimeout(() => setShowNotifBanner(true), 30000);
    }
  }, []);

  const verifierActivitesTerminees = async () => {
    const user = getUser();
    if (!user) return;
    const { data: participations } = await supabase
      .from('participations')
      .select('activite_id, activites(id, titre, date, heure)')
      .eq('user_id', user.id);
    if (!participations) return;
    for (const p of participations) {
      const a = p.activites;
      if (!a || !a.date || !a.heure) continue;
      const dateActivite = new Date(`${a.date}T${a.heure}`);
      const diffH = (new Date() - dateActivite) / (1000 * 60 * 60);
      if (diffH >= 2 && diffH <= 24) {
        const { data: dejaNote } = await supabase.from('notations').select('id').eq('activite_id', a.id).eq('user_id', user.id).single();
        if (!dejaNote) { setNotifPhoto({ id: a.id, titre: a.titre }); break; }
      }
    }
  };

  const verifierRecapHebdo = async () => {
    const user = getUser();
    if (!user) return;
    const dernierRecap = localStorage.getItem('wooki_recap_semaine');
    const maintenant = new Date();
    const debutSemaine = new Date(maintenant);
    debutSemaine.setDate(maintenant.getDate() - maintenant.getDay());
    debutSemaine.setHours(0,0,0,0);
    if (dernierRecap && new Date(dernierRecap) >= debutSemaine) return;
    const finSemaine = new Date(debutSemaine);
    finSemaine.setDate(debutSemaine.getDate() + 7);
    const dateDebut = debutSemaine.toISOString().split('T')[0];
    const dateFin = finSemaine.toISOString().split('T')[0];
    const { data: activites } = await supabase
      .from('activites')
      .select('id, titre, date, heure, lieu, emoji')
      .gte('date', dateDebut)
      .lt('date', dateFin)
      .order('date', { ascending: true })
      .limit(5);
    if (activites && activites.length > 0) {
      setRecapHebdo(activites);
      localStorage.setItem('wooki_recap_semaine', maintenant.toISOString());
    }
  };

  const accepterNotifs = async () => {
    setShowNotifBanner(false);
    if ('Notification' in window) await Notification.requestPermission();
  };

  return (
    <BrowserRouter>
      {showSplash && <Splash onDone={() => setShowSplash(false)} />}

      {/* RÉCAP HEBDO */}
      {recapHebdo && !notifPhoto && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9998, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: '28px 28px 0 0', padding: '24px 24px 48px', width: '100%', maxWidth: 420 }}>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>🌿</div>
              <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.3rem', color: '#1C2B1E', marginBottom: 4 }}>
                Cette semaine près de toi !
              </div>
              <div style={{ fontSize: '0.82rem', color: '#8A9E8D' }}>
                {recapHebdo.length} activité{recapHebdo.length > 1 ? 's' : ''} t'attendent 🎉
              </div>
            </div>
            {recapHebdo.slice(0, 3).map((a, i) => (
              <div key={a.id} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '10px 0', borderBottom: i < Math.min(recapHebdo.length, 3) - 1 ? '1px solid #F3F4F2' : 'none' }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, background: '#EDF7F1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', flexShrink: 0 }}>{a.emoji}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '0.88rem', color: '#1C2B1E' }}>{a.titre}</div>
                  <div style={{ fontSize: '0.7rem', color: '#8A9E8D' }}>
                    {new Date(a.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })} · {a.heure?.slice(0,5)} · {a.lieu?.split(',')[0]}
                  </div>
                </div>
              </div>
            ))}
            <button onClick={() => { setRecapHebdo(null); window.location.href = '/feed'; }} style={{ width: '100%', padding: '16px', background: '#3D9A6A', color: 'white', border: 'none', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '1rem', cursor: 'pointer', marginTop: 20, boxShadow: '0 6px 20px rgba(61,154,106,0.35)' }}>
              🌿 Voir toutes les activités !
            </button>
            <button onClick={() => setRecapHebdo(null)} style={{ width: '100%', padding: '13px', background: 'transparent', color: '#8A9E8D', border: 'none', fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer', marginTop: 8 }}>
              Plus tard
            </button>
          </div>
        </div>
      )}

      {/* BANNER NOTIFICATIONS */}
      {showNotifBanner && (
        <div style={{ position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)', width: '90%', maxWidth: 380, background: 'white', borderRadius: 20, padding: '16px 18px', zIndex: 9997, boxShadow: '0 8px 32px rgba(28,43,30,0.2)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: '2rem', flexShrink: 0 }}>🔔</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '0.92rem', color: '#1C2B1E', marginBottom: 3 }}>Reste informé !</div>
            <div style={{ fontSize: '0.72rem', color: '#8A9E8D', lineHeight: 1.4 }}>Reçois une notif quand une activité est créée près de toi</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
            <button onClick={accepterNotifs} style={{ padding: '7px 14px', background: '#3D9A6A', color: 'white', border: 'none', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '0.75rem', cursor: 'pointer' }}>Activer</button>
            <button onClick={() => setShowNotifBanner(false)} style={{ padding: '6px 14px', background: 'transparent', color: '#8A9E8D', border: 'none', fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.72rem', cursor: 'pointer' }}>Plus tard</button>
          </div>
        </div>
      )}

      {/* POPUP POST-ACTIVITÉ */}
      {notifPhoto && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: '28px 28px 0 0', padding: '28px 24px 48px', width: '100%', maxWidth: 420, textAlign: 'center' }}>
            <div style={{ fontSize: '3rem', marginBottom: 12 }}>📸</div>
            <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.3rem', color: '#1C2B1E', marginBottom: 8 }}>La sortie est terminée !</div>
            <div style={{ fontSize: '0.88rem', color: '#8A9E8D', lineHeight: 1.6, marginBottom: 24 }}>
              Comment s'est passé "{notifPhoto.titre}" ?<br />Partage tes photos avec les autres familles ! 🌿
            </div>
            <a href={`/notation/${notifPhoto.id}`} style={{ display: 'block', width: '100%', padding: '16px', background: '#3D9A6A', color: 'white', border: 'none', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '1rem', cursor: 'pointer', marginBottom: 12, boxShadow: '0 6px 20px rgba(61,154,106,0.35)', textDecoration: 'none' }}>
              📸 Partager mes photos !
            </a>
            <button onClick={() => setNotifPhoto(null)} style={{ width: '100%', padding: '13px', background: 'transparent', color: '#8A9E8D', border: 'none', fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer' }}>
              Plus tard
            </button>
          </div>
        </div>
      )}

      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/inscription" element={<Inscription />} />
        <Route path="/connexion" element={<Connexion />} />
        <Route path="/feed" element={<Feed />} />
        <Route path="/detail/:id" element={<Detail />} />
        <Route path="/creer" element={<CreerActivite />} />
        <Route path="/modifier/:id" element={<PrivateRoute><ModifierActivite /></PrivateRoute>} />
        <Route path="/notation/:id" element={<Notation />} />
        <Route path="/invite/:prenom" element={<Invite />} />
        <Route path="/invite" element={<Invite />} />
        <Route path="/carte" element={<CarteActivites />} />
        <Route path="/cgu" element={<CGU />} />
        <Route path="/famille/:prenom" element={<ProfilPublic />} />
        <Route path="/profil" element={<PrivateRoute><Profil /></PrivateRoute>} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/organiser/:id" element={<PrivateRoute><DevenirOrganisateur /></PrivateRoute>} />
        <Route path="/pro" element={<Pro />} />
        <Route path="/inscription-pro" element={<InscriptionPro />} />
      </Routes>
      <NavBar />
    </BrowserRouter>
  );
}

export default App;